import { BulkImportFoodFragment, CreateFoodNutrientAmountFormInput, NutrientAmountFragment, UnitFragment } from "../../../../types";
import { IMatchResult } from "../../../../utils/import/match";
import { getServingUnits } from "../../../Serving/utils";
import { ImportableFoodEntry, ImportableNutrientAmount, NutrientName } from "./types";
import { round } from "shared-ui/utils/numbers";
import { ALL_FORM_NUTRIENTS } from "shared-ui/NutrientAmount/customNutrientUtils";
import { sortByKey } from "@notemeal/utils-sort";
import { LocaleType } from "@notemeal/locale-utils";

export const GRAMS = "gram(s)";

const KNOWN_NUTRIENT_NAMES: ReadonlySet<string> = new Set(ALL_FORM_NUTRIENTS);

export const areEqual = (val1: string | number | null | undefined, val2: string | number | null | undefined) => {
  if (!val1 && !val2) {
    return true;
  } else if (!val1 || !val2) {
    return false;
  } else if (isNaN(Number(val1)) && isNaN(Number(val2))) {
    return val1 === val2;
  } else if (!isNaN(Number(val1)) && !isNaN(Number(val2))) {
    return Math.abs(Number(val1) - Number(val2)) < 0.0001;
  } else {
    return false;
  }
};

const nutrientAmountsEqual = (
  rowAmounts: readonly ImportableNutrientAmount[],
  existingAmounts: readonly ImportableNutrientAmount[]
): boolean => {
  const existingMap = new Map(existingAmounts.map(n => [n.nutrientName, n.amount]));
  return rowAmounts.every(({ nutrientName, amount }) => areEqual(amount, existingMap.get(nutrientName)));
};

export const equalsExistingEntry = (match: IMatchResult<ImportableFoodEntry, {}>): boolean => {
  if (match.matchedRows.length === 0) {
    return false;
  }

  const servingName = match.row["servingName"];
  const firstMatch = match.matchedRows[0];

  if (!!servingName && servingName !== firstMatch["servingName"]) {
    return false;
  } else if (!areEqual(match.row["weight"] * (match.row["defaultAmount"] || 1), firstMatch.defaultAmount || 1 * firstMatch.weight)) {
    return false;
  }
  return Object.keys(match.row).every(keyString => {
    const key = keyString as keyof ImportableFoodEntry;
    if (key === "weight" || key === "servingName" || key === "defaultAmount") {
      //these are covered above
      return true;
    }
    if (key === "nutrientAmounts") {
      return nutrientAmountsEqual(match.row.nutrientAmounts, firstMatch.nutrientAmounts);
    }
    return areEqual(match.row[key] as string | number | null | undefined, firstMatch[key] as string | number | null | undefined);
  });
};

export const getServingForImportedFood = (
  servingName: string | null | undefined,
  units: readonly UnitFragment[],
  weight: number,
  defaultAmount: number | null | undefined
) => {
  const partialServing = servingName
    ? {
        units: getUnitFromServingName(servingName, units),
        weight: weight,
        defaultAmount: defaultAmount || 1,
      }
    : {
        units: getUnitFromServingName(null, units),
        weight: 1,
        defaultAmount: weight,
      };
  return {
    ...partialServing,
    usdaWeightSeq: null,
    isDefault: true,
    isRecipeServingOnly: false,
  };
};

export const getUnitFromServingName = (servingName: string | null, units: readonly UnitFragment[]) => {
  if (!servingName) {
    const maybeUnit = units.find(unit => unit.name.startsWith(GRAMS));
    return {
      customUnits: null,
      unitSuffix: null,
      unitPrefix: null,
      unitId: maybeUnit?.id || null,
    };
  }
  const lowerCaseServingName = servingName.toLowerCase();
  const containingUnitIndex = units.findIndex(u => !!lowerCaseServingName.includes(u.name.toLowerCase()));
  if (containingUnitIndex === -1) {
    return {
      customUnits: servingName,
      unitSuffix: null,
      unitPrefix: null,
      unitId: null,
    };
  } else {
    const baseServing = units[containingUnitIndex];
    const baseServingIndex = lowerCaseServingName.indexOf(baseServing.name.toLowerCase());
    const prefix = lowerCaseServingName.substring(0, baseServingIndex);
    const suffix = lowerCaseServingName.substring(prefix.length + baseServing.name.length);
    return {
      customUnits: null,
      unitSuffix: suffix,
      unitPrefix: prefix,
      unitId: baseServing.id,
    };
  }
};

export const getEntryFromNutrientAmounts = (
  nutrientAmounts: readonly NutrientAmountFragment[],
  defaultAmount: number,
  weight: number
): ImportableNutrientAmount[] => {
  const out: ImportableNutrientAmount[] = [];
  nutrientAmounts.forEach(na => {
    if (!KNOWN_NUTRIENT_NAMES.has(na.nutrient.name)) return;
    const amount = round((na.amount * defaultAmount * weight) / 100);
    out.push({ nutrientName: na.nutrient.name as NutrientName, amount });
  });
  return out;
};

export interface AdditionalFoodInfo {
  additionalServings: string;
  photo: string;
}

export const getImportableExistingFoodsFromFoods = (
  foods: readonly BulkImportFoodFragment[] | undefined
):
  | (ImportableFoodEntry &
      AdditionalFoodInfo & {
        id: string;
      })[]
  | undefined => {
  return foods?.map(food => {
    const { name, choPer100g, proPer100g, fatPer100g, nutrientAmounts, defaultServing, servings, thumbnailUrl, id, groceryListCategory } =
      food;
    const weight = defaultServing.defaultAmount * (defaultServing.weight || 1);

    return {
      id,
      name,
      servingName: getServingUnits(defaultServing) || GRAMS,
      weight,
      carbohydrate: round((choPer100g * (weight || 100)) / 100),
      protein: round((proPer100g * (weight || 100)) / 100),
      fat: round((fatPer100g * (weight || 100)) / 100),
      nutrientAmounts: getEntryFromNutrientAmounts(nutrientAmounts, defaultServing.defaultAmount, defaultServing.weight || 1),
      additionalServings: servings.length > 1 ? "Yes" : "No",
      photo: !!thumbnailUrl ? "Yes" : "None uploaded",
      thumbnailUrl,
      groceryListCategoryId: groceryListCategory?.id || null,
    };
  });
};

const getNutrientValueFromEntry = (
  nutrient: NutrientName,
  entry: ImportableFoodEntry,
  importedMap: ReadonlyMap<NutrientName, number>
): number => {
  switch (nutrient) {
    case "Carbohydrate, by difference":
      return entry.carbohydrate;
    case "Protein":
      return entry.protein;
    case "Total lipid (fat)":
      return entry.fat;
    default:
      return importedMap.get(nutrient) ?? 0;
  }
};

export const importedNutrientsForInput = (entry: ImportableFoodEntry): CreateFoodNutrientAmountFormInput[] => {
  const scale = 100 / entry.weight;
  const importedMap = new Map(entry.nutrientAmounts.map(n => [n.nutrientName, n.amount]));
  return ALL_FORM_NUTRIENTS.map(nutrient => ({
    nutrientName: nutrient,
    amount: getNutrientValueFromEntry(nutrient, entry, importedMap) * scale,
  }));
};

export const convertMatchToBaseFoodInput = (
  match: IMatchResult<ImportableFoodEntry, AdditionalFoodInfo>,
  units: readonly UnitFragment[]
) => {
  const { name, weight, carbohydrate, protein, fat, defaultAmount, servingName, thumbnailUrl, groceryListCategoryId } = match.row;

  const serving = getServingForImportedFood(servingName, units, weight, defaultAmount);

  return {
    food: {
      usdaFdcId: null,
      usdaFdcDescription: null,
      usdaFdcFoodCategoryId: null,
      usdaFdcDataType: null,
      mccanceId: null,
      mccanceName: null,
      ausnutId: null,
      ausnutName: null,
      isRecipeIngredientOnly: false,
      excludeFromSuggestions: false,
      name,
      thumbnailUrl,
      groceryListCategoryId,
      choPer100g: (carbohydrate * 100) / weight,
      proPer100g: (protein * 100) / weight,
      fatPer100g: (fat * 100) / weight,
      exchangeTypes: [],
      locales: [],
    },
    servings: [serving],
    nutrientAmounts: [],
    nutrientAmountsManualEntry: importedNutrientsForInput(match.row),
  };
};

export const getOrgFoodsInsertInput = (
  matches: IMatchResult<ImportableFoodEntry, AdditionalFoodInfo>[],
  units: readonly UnitFragment[],
  defaultLocale: LocaleType
) => {
  return matches.map(match => {
    const input = convertMatchToBaseFoodInput(match, units);
    return {
      ...input,
      food: {
        ...input.food,
        source: "manual" as const,
        locales: [defaultLocale],
        foodGroupIds: [],
      },
    };
  });
};

export const getRestaurantFoodsInsertInput = (
  matches: IMatchResult<ImportableFoodEntry, AdditionalFoodInfo>[],
  units: readonly UnitFragment[],
  restaurantId: string
) => {
  return matches.map(match => {
    const input = convertMatchToBaseFoodInput(match, units);
    return {
      ...input,
      restaurantId,
      food: {
        ...input.food,
        brandId: null,
        source: "restaurant" as const,
        foodGroupIds: [],
      },
    };
  });
};

const getMostRecentMatch = (
  match: IMatchResult<ImportableFoodEntry, AdditionalFoodInfo>,
  foods: readonly BulkImportFoodFragment[] | undefined
) => {
  return (
    foods &&
    sortByKey(
      foods.filter(food => match.matchedRows.map(match => match.id).includes(food.id)),
      "updatedAt"
    )[0]
  );
};

const baseGetFoodsUpdateInput = (
  matches: IMatchResult<ImportableFoodEntry, AdditionalFoodInfo>[],
  units: readonly UnitFragment[],
  foods: readonly BulkImportFoodFragment[] | undefined
) => {
  if (!foods || matches.length === 0) {
    return [];
  }
  return matches
    .filter(match => {
      if (!!match) {
        return !equalsExistingEntry(match);
      }
      return false;
    })
    .flatMap(match => {
      const mostRecentMatch = getMostRecentMatch(match, foods);
      if (match.matchedRows.length === 0 || !mostRecentMatch) {
        return [];
      }
      return {
        id: mostRecentMatch.id,
        ...convertMatchToBaseFoodInput(match, units),
      };
    });
};

export const getOrgFoodsUpdateInput = (
  matches: IMatchResult<ImportableFoodEntry, AdditionalFoodInfo>[],
  units: readonly UnitFragment[],
  foods: readonly BulkImportFoodFragment[] | undefined
) => {
  return baseGetFoodsUpdateInput(matches, units, foods).map(input => ({
    ...input,
    food: { ...input.food, source: "manual" as const, foodGroupIds: [] },
  }));
};

export const getRestaurantFoodsUpdateInput = (
  matches: IMatchResult<ImportableFoodEntry, AdditionalFoodInfo>[],
  units: readonly UnitFragment[],
  foods: readonly BulkImportFoodFragment[] | undefined,
  restaurantId: string
) => {
  return baseGetFoodsUpdateInput(matches, units, foods).map(input => ({
    ...input,
    food: {
      ...input.food,
      brandId: null,
      source: "restaurant" as const,
      foodGroupIds: [],
    },
    restaurantId,
  }));
};
